import { cn, Loader, Pill } from 'component-library';
import { ReactNode } from 'react-markdown';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { NO_DATA_PLACEHOLDER } from '@/config/constants';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { getStyleForBenchmark } from '@/utils/getStyleForBenchmark';

import { withSuspenseBoundary } from '../SuspenseBoundary';

export const EcosystemIntegrityIndexPill = withSuspenseBoundary(
  ({ className }: { className?: string }) => {
    const factNames = {
      latest: IndicatorName.ecosystem_integrity_index,
      benchmark: IndicatorName.ecosystem_integrity_index_benchmark,
    };

    const facts = usePlotFacts<number>(Object.values(factNames));
    const latestFact = getFactFromFactArray<number>(facts, factNames.latest);

    const highBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    });
    const moderateBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    });
    const lowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    });
    const veryLowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.very_low,
    });

    const benchmarks = [
      lowBenchmarkFact?.value,
      veryLowBenchmarkFact?.value,
      moderateBenchmarkFact?.value,
      highBenchmarkFact?.value,
    ];

    const benchmark = useBenchmarkForFact(benchmarks, latestFact?.value, [
      BenchmarkType.very_low,
      BenchmarkType.low,
      BenchmarkType.moderate,
      BenchmarkType.high,
    ]);

    if (isNullish(latestFact?.value)) {
      return <PillWrapper className={cn(className, 'px-0')}>{NO_DATA_PLACEHOLDER}</PillWrapper>;
    }

    const formattedEii = !isNullish(latestFact?.value) ? getDisplayNumber(latestFact?.value ?? '') : null;

    if (!benchmark) {
      return <PillWrapper className={cn(className, 'typography-body1semibold')}>{formattedEii}</PillWrapper>;
    }

    const style = getStyleForBenchmark(benchmark);

    return (
      <PillWrapper
        className={className}
        style={{
          color: style?.text,
          backgroundColor: style?.primary,
        }}
      >
        {formattedEii}
      </PillWrapper>
    );
  },
  <Loader className='h-fit min-h-fit' />,
);

const PillWrapper = ({
  className,
  style,
  children,
}: {
  className?: string;
  style?: { color?: string; backgroundColor?: string };
  children: ReactNode;
}) => {
  return (
    <Pill
      className={cn('typography-body1 font-bold w-fit rounded-2xl py-2 px-4', className)}
      style={{
        color: style?.color,
        backgroundColor: style?.backgroundColor,
      }}
    >
      {children}
    </Pill>
  );
};
